import {
  BID_STATUS,
  BID_TECHNOLOGY_TYPE,
  COMPANY_CREDIT_PROFILE,
  CONTRACT_TYPE,
  ChargingEnergyResponsibility,
  CreditSupportUnit,
  DispatchRights,
  MeasurementBasis,
  PRODUCT_TYPE,
  PriceUnit,
  REC_PROGRAM,
  SettlementMethod,
  Side,
  TbVariant,
  VolumeUnit,
} from '@/utils/enums';

import { baseApi } from './baseApi';
import {
  ApiTags,
  AuditTagIds,
  CommonTagIds,
  MarketDepthTagIds,
  ProjectTagIds,
} from './tags';
import { BessMatchingCriteriaFlags, MatchingCriteriaFlags } from './types';

export type BidBasicDetails = {
  id: number;
  ticker: string;
  statusId: BID_STATUS;
  statusName: string;
  productTypeId: string;
  productTypeName: string;
  technologyId: BID_TECHNOLOGY_TYPE;
  technologyName: string;
  isoId: string;
  isoName: string;
  settlementPoint: string;
  /** The bid's energy volume. Null on a BESS bid, which is sized by `contractedCapacity`. */
  requestedVolume: number | null;
  /** The BESS bid's Contracted Capacity in MW. Null on an energy bid. */
  contractedCapacity: number | null;
  /** The unit of the bid's size, whichever field holds it, resolved by the backend. */
  volumeUnit: VolumeUnit;
  fixedPrice: number;
  /** The unit of `fixedPrice`, resolved by the backend from the product type. */
  priceUnit: PriceUnit;
  fixedPriceMin: number;
  fixedPriceMax: number;
  tenorStart: string;
  tenorMonths: number;
  internalReference: string;
  contractTypeId: CONTRACT_TYPE;
  contractTypeName: string;
  creditProfileId: COMPANY_CREDIT_PROFILE;
  creditProfileName: string;
  applicableRecProgramId: REC_PROGRAM;
  applicableRecProgramName: string;
  created: string;
  updated: string;
} & BidPositionTags;

export type BidPositionTags = {
  matching247: boolean;
  basisSharing: boolean;
  bessOption: boolean;
  capacityRights: boolean;
  colocationOption: boolean;
  escalator: boolean;
  feocCompliant: boolean;
  financingPartyConsentRequired: boolean;
  fullDeliverability: boolean;
  itcSafeHarbored: boolean;
  nodalSettlement: boolean;
  priceCap: boolean;
  priceCollar: boolean;
  priceFloor: boolean;
  realTimeDataAccess: boolean;
  ssa: boolean;
  suretyBond: boolean;
  taxCreditTransferability: boolean;
  upfrontPayment: boolean;
  upsideRevenueShare: boolean;
};

export type BidDTO = {
  id: number;
  companyId: string;
  companyName: string;
  ticker: string;
  contractLegalEntity: string;
  statusId: BID_STATUS;
  statusName: string;
  productTypeId: string;
  productTypeName: string;
  technologyId: string;
  technologyName: string;
  contractTypeId: string;
  contractTypeName: string;
  feePayingPartyId: string;
  feePayingPartyName: string;
  isoId: string;
  isoName: string;
  /** The bid's energy volume. Null on a BESS bid, which is sized by `contractedCapacity`. */
  requestedVolume: number | null;
  /** The BESS bid's Contracted Capacity in MW. Null on an energy bid. */
  contractedCapacity: number | null;
  /** The unit of the bid's size, whichever field holds it, resolved by the backend. */
  volumeUnit: VolumeUnit;
  hubZoneMatrixId?: number;
  settlementPoint: string;
  settlementIndexId: string;
  settlementIndexName: string;
  fixedPriceMin: number;
  fixedPriceMax: number;
  fixedPrice: number;
  /** The unit of `fixedPrice`, resolved by the backend from the product type. */
  priceUnit: PriceUnit;
  tenorStart: string;
  tenorMonths: number;
  internalReference: string;
  targetCod?: string;
  guaranteedCod?: string;
  countryId?: number;
  stateId?: number;
  stateName?: number;
  cityName?: string;
  negativePricingMethodId?: string;
  negativePricingMethodName?: string;
  performanceGuaranteeMethodId?: string;
  performanceGuaranteeMethodName?: string;
  applicableRecProgramId: string;
  applicableRecProgramName: string;
  applicableTrackingSystemId: string;
  applicableTrackingSystemName: string;
  recDeliveryMethodologyId: string;
  recDeliveryMethodologyName: string;
  creditProfileId: COMPANY_CREDIT_PROFILE;
  creditProfileName: string;
  buyerPostCodCreditSupport?: number;
  creditDescription?: string;
  expectedSettlementMin?: number;
  expectedSettlementMax?: number;
  downsideRiskMin?: number;
  downsideRiskMax?: number;
  historicalNodalLmeMin?: number;
  historicalNodalLmeMax?: number;
  impliedCarbonPriceMin?: number;
  impliedCarbonPriceMax?: number;
  carbonFreeEnergyMin?: number;
  carbonFreeEnergyMax?: number;
  created: string;
  updated: string;
  createdBy: string;
  updatedBy: string;
  // BESS fields. Null on an energy bid. Optional, so fixtures built before these fields existed
  // still type-check.
  settlementMethodId?: SettlementMethod | null;
  /** Null on a hub bid, and on a nodal bid that names no node. */
  settlementNodeId?: number | null;
  settlementNodeName?: string | null;
  tbVariantId?: TbVariant | null;
  dispatchRightsId?: DispatchRights | null;
  scopeEnergy?: boolean | null;
  scopeCapacity?: boolean | null;
  scopeAncillary?: boolean | null;
  scopeTransmission?: boolean | null;
  scopeDemandResponse?: boolean | null;
  scopeBlackstart?: boolean | null;
  settlementMethodIsMc?: boolean | null;
  tbVariantIsMc?: boolean | null;
  dispatchRightsIsMc?: boolean | null;
  productScopeIsMc?: boolean | null;
  variableOmIncluded?: boolean | null;
  variableOmRate?: number | null;
  sharingThreshold?: number | null;
  sharePercentage?: number | null;
  /** The battery duration the buyer stated. Null when the bid states none. */
  durationHours?: number | null;
  chargingEnergyResponsibilityId?: ChargingEnergyResponsibility | null;
  dailyCycleLimit?: number | null;
  annualCycleLimit?: number | null;
  cycleSpillover?: boolean | null;
  guaranteedAvailability?: number | null;
  measurementBasisId?: MeasurementBasis | null;
  /** The unit of `buyerPostCodCreditSupport` on a BESS bid, resolved by the backend. */
  creditSupportUnit?: CreditSupportUnit | null;
} & BidPositionTags &
  MatchingCriteriaFlags;

export type GetBidsParams = {
  companyId: string;
  statuses?: BID_STATUS[];
  productType?: PRODUCT_TYPE;
};

export type GetBidParams = {
  id: number;
  companyId: string;
  sideView: string;
};

export type CreateBidRequest = {
  status: string;
  productType: string;
  technology: string;
  contractType: string;
  iso: string;
  /** Sent on an energy bid only. A BESS bid is sized by `contractedCapacity`. */
  requestedVolume?: number;
  fixedPriceMin: number;
  fixedPriceMax: number;
  tenorStart: string;
  tenorMonths: number;
  internalReference?: string;
  applicableRecProgram: string;
  applicableTrackingSystem: string;
  recDeliveryMethodology: string;
  creditProfile: string;
  hubZoneMatrixId?: number;
  settlementPoint?: string;
  settlementIndex?: string;
  buyerPostCodCreditSupport?: number;
  creditDescription?: string;
  targetCod?: string;
  guaranteedCod?: string;
  countryId?: number;
  stateId?: number;
  cityName?: string;
  negativePricingMethod?: string;
  performanceGuaranteeMethod?: string;
  expectedSettlementMin?: number;
  expectedSettlementMax?: number;
  downsideRiskMin?: number;
  downsideRiskMax?: number;
  historicalNodalLmeMin?: number;
  historicalNodalLmeMax?: number;
  impliedCarbonPriceMin?: number;
  impliedCarbonPriceMax?: number;
  carbonFreeEnergyMin?: number;
  carbonFreeEnergyMax?: number;
  // BESS fields. An energy bid sends none of them.
  settlementMethod?: SettlementMethod;
  tbVariant?: TbVariant;
  dispatchRights?: DispatchRights;
  scopeEnergy?: boolean;
  scopeCapacity?: boolean;
  scopeAncillary?: boolean;
  scopeTransmission?: boolean;
  scopeDemandResponse?: boolean;
  scopeBlackstart?: boolean;
  variableOmIncluded?: boolean;
  variableOmRate?: number;
  sharingThreshold?: number;
  sharePercentage?: number;
  contractedCapacity?: number;
  durationHours?: number;
  chargingEnergyResponsibility?: ChargingEnergyResponsibility;
  dailyCycleLimit?: number;
  annualCycleLimit?: number;
  cycleSpillover?: boolean;
  guaranteedAvailability?: number;
  measurementBasis?: MeasurementBasis;
  /*
   * `priceEscalation` is deliberately not typed, so the form cannot send it. The backend stores
   * a missing value as 0. `settlementNodeId` is not typed either: the bid form names no node,
   * and an update that omits it clears a stored one.
   */
} & BidPositionTags &
  MatchingCriteriaFlags &
  BessMatchingCriteriaFlags;

export type CreateBidParams = {
  data: CreateBidRequest;
  companyId: string;
  offerId?: string;
};

/**
 * Product type and contract type are immutable after creation, but their matching criteria
 * flags are not omitted here — an update is a full replacement, so every flag (the six energy
 * flags, and the four BESS flags on a BESS bid) must be sent back or the server re-tightens the
 * ones it does not receive.
 */
export type UpdateBidRequest = Omit<
  CreateBidRequest,
  'productType' | 'contractType' | 'countryId'
>;

export type UpdateBidParams = {
  data: UpdateBidRequest;
  companyId: string;
  id: number;
};

export type DeleteBidParams = {
  id: number;
  companyId: string;
};

/**
 * One of the seller's projects that could back an offer matching a given bid.
 *
 * `availableCapacityAc` is reported for display only. It is not an eligibility criterion, and no
 * layer validates an offer's capacity against a project's remaining capacity, so options must not
 * be filtered by it.
 */
export type EligibleProjectResponse = {
  id: string;
  name: string;
  technologyId: string;
  technologyName: string;
  isoId: string;
  isoName: string;
  projectOperationalStatusId: string | null;
  megawattAc: number;
  availableCapacityAc: number;
};

export type GetEligibleProjectsParams = {
  bidId: number;
  companyId: string;
};

const BASE_URL = '/api/v1/bids';

export const bidApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getBids: build.query<BidBasicDetails[], GetBidsParams>({
      query: ({ companyId, statuses }) => {
        const searchParams = new URLSearchParams();

        if (statuses) {
          statuses.forEach(status => searchParams.append('statuses', status));
        }

        const queryString = searchParams.toString();
        const suffix = queryString ? `?${queryString}` : '';
        const url = `${BASE_URL}${suffix}`;

        return {
          url,
          headers: {
            'X-Company-Id': companyId,
          },
        };
      },
      providesTags: (result, error, { companyId }) => [
        {
          type: ApiTags.BIDS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
      ],
    }),
    getBid: build.query<BidDTO, GetBidParams>({
      query: ({ id, companyId, sideView }) => ({
        url: `${BASE_URL}/${id}`,
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      providesTags: (result, error, { id }) => [{ type: ApiTags.BIDS, id }],
    }),
    getEligibleProjects: build.query<
      EligibleProjectResponse[],
      GetEligibleProjectsParams
    >({
      query: ({ bidId, companyId }) => ({
        url: `${BASE_URL}/${bidId}/eligible-projects`,
        headers: {
          'X-Company-Id': companyId,
          // The endpoint rejects any side view other than SELLER.
          'X-Side-View': Side.SELLER,
        },
      }),
      // Eligibility is a function of the seller's projects, so onboarding, editing or deleting one
      // changes this answer. Tagged so the project mutations already invalidating PROJECTS refresh it,
      // rather than leaving the staging panel to decide against a list that predates the change.
      //
      // Carries the ACTIVE id rather than being id-less: an eligible project is by definition a
      // published one, and an invalidation naming an id only reaches providers registered under that
      // id. Id-less providers are invisible to `deleteProject`, which invalidates
      // `{ PROJECTS, DRAFT | ACTIVE }` - so an id-less tag here left a deleted project eligible until
      // something else happened to refresh the list. The id-less invalidations from
      // `publishProject`, `updateProject` and `updateAndPublishProject` still reach this.
      providesTags: [{ type: ApiTags.PROJECTS, id: ProjectTagIds.ACTIVE }],
    }),
    createBid: build.mutation<BidDTO, CreateBidParams>({
      query: ({ data, companyId }) => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      invalidatesTags: (result, error, { companyId, offerId = '' }) => [
        {
          type: ApiTags.BIDS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: offerId
            ? `${MarketDepthTagIds.MATCHING_BIDS}-${offerId}`
            : MarketDepthTagIds.MATCHING_BIDS,
        },
        { type: ApiTags.MARKET_ACTIVITY },
        ...(result
          ? [
              {
                type: ApiTags.AUDIT as const,
                id: `${AuditTagIds.BID}-${result.id}`,
              },
            ]
          : []),
      ],
    }),
    updateBid: build.mutation<BidDTO, UpdateBidParams>({
      query: ({ data, companyId, id }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'PUT',
        body: data,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      invalidatesTags: (result, error, { companyId, id }) => [
        { type: ApiTags.BIDS, id },
        {
          type: ApiTags.BIDS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_BIDS,
        },
        { type: ApiTags.MARKET_ACTIVITY },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.BID}-${id}`,
        },
      ],
    }),
    deleteBid: build.mutation<void, DeleteBidParams>({
      query: ({ id, companyId }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      invalidatesTags: (result, error, { companyId, id }) => [
        { type: ApiTags.BIDS, id },
        {
          type: ApiTags.BIDS,
          id: `${CommonTagIds.LIST}-${companyId}`,
        },
        {
          type: ApiTags.MARKET_DEPTH,
          id: MarketDepthTagIds.MATCHING_BIDS,
        },
        {
          type: ApiTags.AUDIT,
          id: `${AuditTagIds.BID}-${id}`,
        },
      ],
    }),
  }),
});

export const {
  useGetBidsQuery,
  useGetBidQuery,
  useGetEligibleProjectsQuery,
  useCreateBidMutation,
  useUpdateBidMutation,
  useDeleteBidMutation,
} = bidApi;
